import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import {
  deleteAttackSurfaceAsset, listAttackSurface, listAttackSurfaceChanges, attackSurfaceSummary,
  runAttackSurfaceDiscovery, scheduleAttackSurfaceMonitoring
} from 'api/features';
import {
  alpha, Alert, Box, Button, Chip, IconButton, InputAdornment, LinearProgress, Pagination, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import { DeleteOutline as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';

const GREEN = '#22c55e';
const MONO = 'var(--fuzzscan-ui-font-mono)';
const PAGE_SIZE = 12;

const TYPES = [['all', 'All'], ['subdomain', 'Subdomains'], ['ip', 'IPs'], ['service', 'Services'], ['endpoint', 'Endpoints'], ['cloud_resource', 'Cloud']];
const TYPE_LABEL = { subdomain: 'Subdomain', ip: 'IP address', service: 'Service', endpoint: 'Endpoint', cloud_resource: 'Cloud resource' };
const LEVEL = {
  critical: { label: 'CRITICAL', color: '#ef4444' }, high: { label: 'HIGH', color: '#f97316' },
  medium: { label: 'MEDIUM', color: '#f59e0b' }, low: { label: 'LOW', color: GREEN }
};
const EVENT = {
  new: { label: 'New', color: '#4ade80' }, changed: { label: 'Changed', color: '#fcd34d' }, removed: { label: 'Removed', color: '#94a3b8' }
};
const FREQ = { hourly: 'HOURLY', daily: 'DAILY', weekly: 'WEEKLY' };

const levelOf = (a) => {
  const s = Number(a.exposure_score || 0);
  return a.exposure_level && LEVEL[a.exposure_level] ? a.exposure_level : s >= 85 ? 'critical' : s >= 65 ? 'high' : s >= 35 ? 'medium' : 'low';
};
const ago = (iso) => {
  if (!iso) return '—';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  const d = Math.floor(m / 1440);
  return d === 1 ? 'Yesterday' : d < 14 ? `${d} days ago` : new Date(iso).toLocaleDateString();
};
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const th = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontWeight: 600, borderBottom: 1, borderColor: 'divider' };

export default function AttackSurfacePage() {
  const { currentTargetId, targets } = useFuzzScanTarget();
  const [scope, setScope] = useState('target'); // 'target' | 'all'
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({ kpis: {}, change_counts: {}, monitoring: null });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [eventsShown, setEventsShown] = useState(6);
  const [page, setPage] = useState(1);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const toast = (severity, message) => setSnack({ open: true, severity, message });

  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)), [targets, currentTargetId]);
  const scoped = scope === 'target' && currentTargetId;
  const params = useMemo(() => (scoped ? { user_target: currentTargetId } : {}), [scoped, currentTargetId]);
  const name = scoped ? userTarget?.name || userTarget?.target?.value : 'all your targets';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, e] = await Promise.all([listAttackSurface(params), attackSurfaceSummary(params), listAttackSurfaceChanges(params)]);
      setAssets(Array.isArray(a) ? a : a.results || []);
      setSummary(s || { kpis: {}, change_counts: {}, monitoring: null });
      setEvents(Array.isArray(e) ? e : e.results || []);
    } catch (err) {
      toast('error', err.message || 'Failed to load the attack surface.');
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); setPage(1); setEventsShown(6); }, [load]);

  const workspaceIds = useMemo(() => (scoped ? [currentTargetId] : (targets || []).map((t) => t.id)).filter(Boolean), [scoped, currentTargetId, targets]);

  const act = async (fn, okMessage) => {
    if (!workspaceIds.length) { toast('warning', 'Create or select a target first.'); return; }
    setBusy(true);
    try { await Promise.all(workspaceIds.map(fn)); toast('success', okMessage); await load(); }
    catch (err) { toast('error', err.message || 'Something went wrong.'); }
    finally { setBusy(false); }
  };
  const runDiscovery = () => act((id) => runAttackSurfaceDiscovery({ user_target: id, fingerprint: true }), 'Discovery queued.');
  const toggleMonitoring = () => {
    const on = !(summary.monitoring?.enabled);
    return act((id) => scheduleAttackSurfaceMonitoring({ user_target: id, frequency: summary.monitoring?.frequency || 'daily', enabled: on }),
      on ? 'Daily monitoring is on.' : 'Monitoring turned off.');
  };

  const removeAsset = async (id) => {
    setBusy(true);
    try { await deleteAttackSurfaceAsset(id); setAssets((p) => p.filter((a) => String(a.id) !== String(id))); toast('success', 'Asset removed.'); }
    catch (err) { toast('error', err.message || 'Could not remove the asset.'); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => (type === 'all' || a.asset_type === type)
      && (!q || `${a.value} ${a.asset_type} ${(a.technologies || a.tech || []).join(' ')}`.toLowerCase().includes(q)));
  }, [assets, type, search]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const shownEvents = events.filter((e) => eventFilter === 'all' || e.change_type === eventFilter);

  const cc = summary.change_counts || {};
  const mon = summary.monitoring;
  const counters = [
    ['New assets', cc.new ?? 0, EVENT.new.color, 'in the last 7 days'],
    ['Changed', cc.changed ?? 0, EVENT.changed.color, 'ports or technology'],
    ['Removed', cc.removed ?? 0, EVENT.removed.color, 'no longer reachable'],
    ['New critical', cc.new_critical ?? 0, LEVEL.critical.color, 'needs attention']
  ];

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Attack surface</Typography>
          <Typography variant="body2" color="text.secondary">
            What changed on <Box component="span" sx={{ fontFamily: MONO, color: 'text.primary' }}>{name}</Box> since the last discovery
          </Typography>
        </Box>
        <Stack direction="row" gap={1.2} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup exclusive size="small" value={scope} onChange={(_, v) => v && setScope(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2, minHeight: 44 }, '& .Mui-selected': { bgcolor: `${alpha(GREEN, 0.16)} !important`, color: `${GREEN} !important` } }}>
            <ToggleButton value="target" disabled={!currentTargetId}>This target</ToggleButton>
            <ToggleButton value="all">All targets</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" disabled={busy} onClick={runDiscovery}
            sx={{ minHeight: 44, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Run discovery now</Button>
        </Stack>
      </Stack>
      {(loading || busy) && <LinearProgress sx={{ mb: 2 }} color="success" />}

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} sx={{ mb: 3 }}>
        <Box sx={{ flex: '1 1 360px', border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Continuous monitoring</Typography>
            <Chip size="small" label={mon?.enabled ? `ON · ${FREQ[mon.frequency] || mon.frequency}` : 'OFF'}
              sx={{ fontWeight: 700, bgcolor: alpha(mon?.enabled ? GREEN : '#64748b', 0.16), color: mon?.enabled ? '#4ade80' : '#94a3b8' }} />
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            {[['Last run', mon?.last_run_at ? ago(mon.last_run_at) : 'Never'], ['Next run', mon?.enabled ? when(mon.next_run_at) : '—']].map(([k, v]) => (
              <Box key={k} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, px: 1.8, py: 1.4 }}>
                <Typography variant="caption" color="text.secondary">{k}</Typography>
                <Typography sx={{ fontWeight: 600, mt: 0.3 }}>{v}</Typography>
              </Box>
            ))}
          </Box>
          <Button size="small" disabled={busy} onClick={toggleMonitoring} sx={{ color: GREEN, fontWeight: 700 }}>
            {mon?.enabled ? 'Turn monitoring off' : 'Turn on daily monitoring'}
          </Button>
        </Box>
        <Box sx={{ flex: '2 1 560px', display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {counters.map(([label, value, color, note]) => (
            <Box key={label} sx={{ border: 1, borderColor: 'divider', borderTop: `3px solid ${color}`, borderRadius: 3.5, p: 2, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography sx={{ fontSize: 38, fontWeight: 800, fontFamily: MONO, color, lineHeight: 1 }}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{note}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2.5} alignItems="flex-start">
        {/* timeline */}
        <Box sx={{ flex: '1 1 420px', minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper' }}>
          <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Timeline</Typography>
            <Stack direction="row" gap={0.6}>
              {[['all', 'All'], ['new', 'New'], ['changed', 'Changed'], ['removed', 'Removed']].map(([id, label]) => (
                <Chip key={id} size="small" clickable label={label} onClick={() => { setEventFilter(id); setEventsShown(6); }}
                  sx={{ fontWeight: 600, border: 1, borderColor: 'divider', bgcolor: eventFilter === id ? alpha(GREEN, 0.16) : 'transparent', color: eventFilter === id ? GREEN : 'text.secondary' }} />
              ))}
            </Stack>
          </Stack>
          {shownEvents.length === 0 && <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : 'No changes yet. Run a discovery to start tracking.'}</Typography>}
          {shownEvents.slice(0, eventsShown).map((e, i, arr) => {
            const ev = EVENT[e.change_type] || EVENT.changed;
            return (
              <Stack key={e.id} direction="row" gap={1.8}>
                <Stack alignItems="center">
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ev.color, mt: 0.7 }} />
                  {i < arr.length - 1 && <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5 }} />}
                </Stack>
                <Box sx={{ pb: 2, flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{e.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{ago(e.created_at)}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ color: ev.color, fontWeight: 600 }}>{ev.label}</Box> · {TYPE_LABEL[e.asset_type] || e.asset_type}{e.severity && e.severity !== 'info' ? ` · ${e.severity} severity` : ''}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
          {shownEvents.length > eventsShown && <Button size="small" onClick={() => setEventsShown((n) => n + 10)} sx={{ color: GREEN, fontWeight: 700 }}>Show more</Button>}
        </Box>

        {/* inventory */}
        <Box sx={{ flex: '2 1 600px', minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 4, overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 700 }}>Full inventory · {filtered.length} asset{filtered.length === 1 ? '' : 's'}</Typography>
            <TextField size="small" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search assets or technology" sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>
          <Stack direction="row" gap={0.8} flexWrap="wrap" sx={{ px: 2.5, pb: 2 }}>
            {TYPES.map(([id, label]) => (
              <Chip key={id} clickable label={label} onClick={() => { setType(id); setPage(1); }}
                sx={{ fontWeight: 600, border: 1, borderColor: type === id ? alpha(GREEN, 0.5) : 'divider', bgcolor: type === id ? alpha(GREEN, 0.16) : 'transparent', color: type === id ? GREEN : 'text.secondary' }} />
            ))}
          </Stack>
          <TableContainer>
            <Table sx={{ minWidth: 720 }}>
              <TableHead><TableRow>
                <TableCell sx={{ ...th, pl: 2.5 }}>Asset</TableCell><TableCell sx={th}>Exposure</TableCell><TableCell sx={th}>Technology</TableCell>
                <TableCell sx={th}>Ports</TableCell><TableCell sx={th}>First seen</TableCell><TableCell sx={th} />
              </TableRow></TableHead>
              <TableBody>
                {paged.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  {assets.length ? 'No asset matches.' : 'Nothing here yet. Run a discovery to build the inventory.'}</Typography></TableCell></TableRow>}
                {paged.map((a) => {
                  const lv = LEVEL[levelOf(a)];
                  const techs = a.technologies || a.tech || [];
                  const removed = a.status === 'removed' || a.metadata?.status === 'removed';
                  const score = Math.round(Number(a.exposure_score || 0));
                  return (
                    <TableRow key={a.id} hover sx={{ opacity: removed ? 0.55 : 1 }}>
                      <TableCell sx={{ pl: 2.5, py: 1.8 }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{a.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{TYPE_LABEL[a.asset_type] || a.asset_type}{removed ? ' · removed' : ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Stack direction="row" alignItems="center" gap={1.2}>
                          <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover' }}><Box sx={{ height: '100%', width: `${Math.min(100, score)}%`, borderRadius: 3, bgcolor: lv.color }} /></Box>
                          <Chip size="small" label={`${lv.label} · ${score}%`} sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(lv.color, 0.16), color: lv.color }} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {techs.length ? (
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {techs.slice(0, 3).map((t) => <Chip key={t} size="small" label={t} sx={{ height: 22, fontSize: 11 }} />)}
                            {techs.length > 3 && <Chip size="small" label={`+${techs.length - 3}`} sx={{ height: 22, fontSize: 11 }} />}
                          </Stack>
                        ) : <Typography variant="caption" color="text.secondary">Not fingerprinted</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary' }}>{(a.ports || []).join(', ') || '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{ago(a.first_seen_at || a.created_at)}</TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>
                        <Tooltip title="Remove from the attack surface"><span><IconButton size="small" aria-label={`Remove ${a.value}`} disabled={busy} onClick={() => removeAsset(a.id)}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {filtered.length > PAGE_SIZE && (
            <Stack alignItems="center" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Pagination count={Math.ceil(filtered.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} size="small" />
            </Stack>
          )}
        </Box>
      </Stack>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
